import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconChevronDown, IconGrip } from './icons';

/**
 * The shape every ตั้งค่า list page uses.
 *
 * Their settings screens are all the same component underneath — a stack of
 * full-width group cards, each with a name, a count, a collapse chevron and a
 * ••• menu, holding rows that carry a drag grip, a label and their own •••.
 * หมอนวด and ห้องนวด looked like different designs only because I had built
 * them from two different guesses.
 *
 * Class names below are translated from their markup rather than invented:
 *   <div class="w-full flex items-center justify-between">
 *     <div class="ml-2 flex items-center">
 *       <span font-size="1.25rem" font-weight="500">…</span>
 *       <span font-size="0.875rem" color="var(--gray-400)">(# …)</span>
 *   <div class="w-[32px] h-[32px] cursor-pointer bg-gray-50 rounded-sm">
 */
export function SettingGroup({ title, count, countUnit = 'รายการ', menu, children, empty }) {
  const [closed, setClosed] = useState(false);

  return (
    /* Cards sit 16px apart on the original, not 14. The 16px inset is the
       card's own padding — measured on their /setting-room and /setting-staff,
       where the header sits at x=104 against a card edge at x=88 — so the
       header and the row list below carry no horizontal padding of their own. */
    <div className="setting-group mb-4">
      <div className="w-full flex items-center justify-between">
        <div className="ml-2 flex items-center gap-2">
          <span className="group-title">{title}</span>
          <span className="group-count">({count} {countUnit})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="w-[32px] h-[32px] cursor-pointer bg-gray-50 rounded-sm grid place-items-center border-0 text-gray-500"
            title={closed ? 'ขยาย' : 'ย่อ'}
            onClick={() => setClosed((c) => !c)}
          >
            <IconChevronDown size={18} className={closed ? '' : 'flip'} />
          </button>
          {menu}
        </div>
      </div>
      {!closed && (count ? <div className="setting-rows">{children}</div>
        : <div className="py-4 text-sm text-gray-400">{empty || 'ไม่มีรายการ'}</div>)}
    </div>
  );
}

/**
 * One row inside a group: grip, label, and the row's own ••• menu.
 *
 * Measured on their /setting-room, /setting-staff and /setting-role: the rows
 * abut, each outlined in 2px --ink-100, so consecutive rows share one rule and
 * only the first and last corners round off. The height is not set — a 12px/16px
 * inset around a 24px line is what lands their 56px, and their staff rows grow
 * to 60px for the avatar rather than clipping it. `.setting-rows` in App.css
 * carries the border collapsing and the corners.
 */
export function SettingRow({
  label, trailing, menu, gripSize = 18, draggable, onDragStart, onDragOver, onDrop,
}) {
  return (
    <div
      className="setting-row flex items-center gap-2 text-[16px]"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* The grip is what sets a row's height once it is taller than the 24px
          line: their ห้องนวด draws it at 30 and its rows measure 58, their
          สิทธิ์เข้าใช้ draws none and its rows measure 52. */}
      <span className="text-gray-100 cursor-grab flex"><IconGrip size={gripSize} /></span>
      <span className="flex-1 font-normal">{label}</span>
      {trailing}
      {menu}
    </div>
  );
}

/**
 * The ••• the original puts at the end of a row or a group header.
 *
 * The menu is rendered into <body> rather than beside the button. `.setting-rows`
 * caps a group at 210px and scrolls past it — their measurement, not ours — and
 * a scroll container clips absolutely positioned children, so the menu opened
 * inside the card and got cut off at its edge. Fixed positioning against the
 * button's viewport rect is the only way out of a scroll box without giving up
 * the scrolling.
 *
 * Scroll and resize events recalculate the fixed position so the menu remains
 * attached to the ••• it belongs to.
 */
export function RowMenu({ items, trigger, label = 'เพิ่มเติม' }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const buttonRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const place = () => {
      const r = buttonRef.current.getBoundingClientRect();
      // Flip above the button when there is not room beneath it, so a row near
      // the bottom of the window still shows a whole menu.
      const below = window.innerHeight - r.bottom;
      setAt({
        right: Math.round(window.innerWidth - r.right),
        ...(below < 180
          ? { bottom: Math.round(window.innerHeight - r.top) + 4 }
          : { top: Math.round(r.bottom) + 4 }),
      });
    };
    place();

    // Keep the menu anchored while a scrollable settings group or the board
    // moves. Closing on every scroll also swallowed the same click that opened
    // a menu when the browser first brought its trigger into view.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <span
      className="row-menu"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        ref={buttonRef}
        className="icon-plain"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger || '•••'}
      </button>
      {open && at && createPortal(
        <>
          <div className="row-menu-scrim" onMouseDown={() => setOpen(false)} />
          <div className="row-menu-pop floating" style={at} role="menu">
            {items.filter(Boolean).map((it) => (
              <button
                type="button"
                key={it.label}
                className={it.danger ? 'danger' : ''}
                role="menuitem"
                onClick={async () => { setOpen(false); await it.onClick(); }}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}
